/** Person and company booker editors share the reservation Guest Profile dialog. */
import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import GuestProfileDialog from "./GuestProfileDialog";
import BookerCompanyForm, {
  BookerTypeTabs, EMPTY_BOOKER_COMPANY, companyBookerContactRequired,
} from "./BookerCompanyForm";

const EMPTY_BOOKER = {
  salutation: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  second_last_name: "",
  gender: "",
  birth_date: "",
  email: "",
  phone: "",
  country: "",
  city: "",
  address: "",
  postal_code: "",
  document_type: "",
  document_number: "",
  is_vip: false,
  vip_level: "",
  customer_description: "",
  photo_url: "",
  status: "active",
};

function requiredBookerFields(settings) {
  let raw = settings?.required_customer_fields_reservation;
  if (typeof raw === "string") {
    try { raw = JSON.parse(raw); } catch { raw = null; }
  }
  return Array.isArray(raw) ? raw : Array.isArray(raw?.booker) ? raw.booker : [];
}

function personDraft(source) {
  return {
    ...EMPTY_BOOKER, ...source,
    ...Object.fromEntries(Object.entries(EMPTY_BOOKER).map(([name, fallback]) => [name, source?.[name] ?? fallback])),
  };
}

const BookerCustomerCreatePopup = ({
  show, businessUnitId, onClose, mode = "create", initialData = null,
  initialType = "person", initialCompany = null, initialContactName = "",
  reservationGroupId, isRealGroup = false, onSaved,
}) => {
  const [contentReady, setContentReady] = useState(false);
  const [requiredFields, setRequiredFields] = useState([]);
  const [contactRequired, setContactRequired] = useState(true);
  const [customerType, setCustomerType] = useState(initialType);
  const [formData, setFormData] = useState(EMPTY_BOOKER);
  const [companyData, setCompanyData] = useState(EMPTY_BOOKER_COMPANY);
  const [contactName, setContactName] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [profileLoadError, setProfileLoadError] = useState("");
  const busyRef = useRef(false);
  // Keep separate identities per customer type, including after a failed assignment.
  const savedIdsRef = useRef({ person: null, company: null });
  const resolvedBusinessUnitId = businessUnitId || initialCompany?.business_unit_id || initialData?.business_unit_id;

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setContentReady(false);
    setFeedback("");
    setProfileLoadError("");
    setCustomerType(initialType === "company" ? "company" : "person");
    setContactName(initialContactName || "");
    setFormData(personDraft(initialType === "person" ? initialData : null));
    setCompanyData({ ...EMPTY_BOOKER_COMPANY, ...(initialType === "company" ? initialCompany : null) });
    savedIdsRef.current = {
      person: mode === "edit" && initialType === "person" ? initialData?.id : null,
      company: mode === "edit" && initialType === "company" ? initialCompany?.id : null,
    };

    async function load() {
      const settingsPromise = resolvedBusinessUnitId
        ? axios.get("/api/hotel-settings", { params: { business_unit_id: resolvedBusinessUnitId } })
        : Promise.resolve({ data: {} });
      const profileId = savedIdsRef.current[initialType];
      const profilePromise = profileId
        ? axios.get(initialType === "company" ? `/api/booker-companies/${profileId}` : `/api/customers/${profileId}`,
          { params: { business_unit_id: resolvedBusinessUnitId } })
        : Promise.resolve(null);
      const [settingsResult, profileResult] = await Promise.allSettled([settingsPromise, profilePromise]);
      if (cancelled) return;
      if (settingsResult.status === "fulfilled") {
        setRequiredFields(requiredBookerFields(settingsResult.value.data));
        setContactRequired(companyBookerContactRequired(settingsResult.value.data));
      } else {
        setRequiredFields([]);
        setContactRequired(true);
      }
      if (profileResult.status === "fulfilled" && profileResult.value?.data) {
        if (initialType === "company") setCompanyData({ ...EMPTY_BOOKER_COMPANY, ...profileResult.value.data });
        else setFormData(personDraft(profileResult.value.data));
      } else if (profileId) {
        setProfileLoadError(initialType === "company"
          ? "The full company profile could not be loaded. Close this window and reopen it before saving to protect the existing profile details."
          : "The full guest profile could not be loaded. Close this window and reopen it before saving to protect the existing profile details.");
      }
      setContentReady(true);
    }
    load();
    return () => { cancelled = true; };
  }, [show, mode, initialType, initialData, initialCompany, initialContactName, resolvedBusinessUnitId]);

  const patchBookerAssignment = async (assignment, updatedBy) => {
    const endpoint = isRealGroup
      ? `/api/reservation-groups/${reservationGroupId}`
      : `/api/reservation-draft-groups/${reservationGroupId}/booker`;
    await axios.patch(endpoint, { ...assignment, updated_by: updatedBy });
  };

  const finishAndClose = async () => {
    if (typeof onSaved === "function") await onSaved();
    else onClose?.();
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (busyRef.current) return;
    if (profileLoadError) {
      setFeedback(profileLoadError);
      return;
    }
    const isCompany = customerType === "company";
    if (!reservationGroupId) {
      setFeedback("The reservation group is missing. Close this window and reopen the booker flow.");
      return;
    }
    if (!resolvedBusinessUnitId) {
      setFeedback("The business unit is missing. Close this window and reopen the booker flow.");
      return;
    }
    if (isCompany && !companyData.name?.trim()) {
      setFeedback("Enter the company name.");
      return;
    }
    if (isCompany && companyData.status !== "active") {
      setFeedback("A company must be active to be assigned as a booker. Change its status to Active before saving.");
      return;
    }
    if (isCompany && contactRequired && !contactName.trim()) {
      setFeedback("Enter the full name of the person who contacted you to book for this company.");
      return;
    }
    if (!isCompany) {
      const missing = requiredFields.filter((name) => formData[name] == null || String(formData[name]).trim() === "");
      if (missing.length) {
        setFeedback({ key: "Complete the required fields: {fields}.", fields: missing });
        return;
      }
    }

    busyRef.current = true;
    setSaving(true);
    setFeedback("");
    try {
      let id = savedIdsRef.current[customerType];
      const payload = isCompany
        ? Object.fromEntries(Object.keys(EMPTY_BOOKER_COMPANY).map((name) => [name, String(companyData[name] ?? "").trim() || null]))
        : {
          ...formData,
          document_type: formData.document_type || null,
          document_number: formData.document_number || null,
          salutation: formData.salutation || null,
          gender: formData.gender || null,
          vip_level: formData.vip_level || null,
          birth_date: !formData.birth_date || Number.isNaN(new Date(formData.birth_date).getTime()) ? null : formData.birth_date,
        };
      payload.business_unit_id = resolvedBusinessUnitId;
      const endpoint = isCompany ? "/api/booker-companies" : "/api/customers";
      if (id) {
        await axios.put(`${endpoint}/${id}`, payload);
      } else {
        const { data: created } = await axios.post(endpoint, payload);
        id = created?.id;
        if (!id) throw new Error("The saved profile did not include an id.");
        savedIdsRef.current[customerType] = id;
      }
      await patchBookerAssignment(isCompany
        ? { booker_id: null, booker_company_id: id, booker_contact_name: contactName.trim() || null }
        : { booker_id: id, booker_company_id: null, booker_contact_name: null },
      mode === "edit" ? "BookerPopupEdit" : "BookerPopupCreate");
      await finishAndClose();
    } catch (error) {
      setFeedback(error?.response?.data?.error || "The booker could not be saved. Please try again.");
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (busyRef.current) return;
    if (!reservationGroupId) {
      setFeedback("The reservation group is missing. Close this window and reopen the booker flow.");
      return;
    }
    busyRef.current = true;
    setRemoving(true);
    setFeedback("");
    try {
      await patchBookerAssignment({ booker_id: null, booker_company_id: null, booker_contact_name: null }, "BookerPopupRemove");
      await finishAndClose();
    } catch (error) {
      setFeedback(error?.response?.data?.error || "The booker could not be removed. Please try again.");
    } finally {
      busyRef.current = false;
      setRemoving(false);
    }
  };

  if (!show || !contentReady || typeof document === "undefined") return null;
  const isCompany = customerType === "company";
  const changeField = (setter) => (event) => {
    const { name, value } = event.target;
    setFeedback("");
    setter((previous) => ({ ...previous, [name]: value }));
  };

  return (
    <GuestProfileDialog
      title={mode === "edit" ? "Edit Booker" : "Create Booker"}
      subtitle={mode === "edit"
        ? "Update the person or company responsible for booking this reservation group."
        : "Create a person or company profile and assign it directly as the reservation booker."}
      contextLabel={isCompany ? "Company booker" : "Person booker"}
      createLabel="Create booker" removeLabel="Remove booker" mode={mode}
      formData={formData} handleChange={changeField(setFormData)}
      handleSelectChange={(selected, action) => {
        setFeedback("");
        setFormData((previous) => ({ ...previous, [action.name]: selected?.value ?? "" }));
      }}
      requiredFields={isCompany ? ["name", ...(contactRequired ? ["booker_contact_name"] : [])] : requiredFields}
      onClose={() => { if (!busyRef.current) onClose?.(); }} onSubmit={handleSave} onRemove={handleRemove}
      saving={saving} removing={removing} feedback={feedback || profileLoadError}
      submitDisabled={Boolean(profileLoadError)}
      beforeFields={<BookerTypeTabs value={customerType} onChange={(type) => { setCustomerType(type); setFeedback(""); }} />}
    >
      {isCompany ? <BookerCompanyForm formData={companyData} onChange={changeField(setCompanyData)}
        contactName={contactName} contactRequired={contactRequired}
        onContactChange={(event) => { setContactName(event.target.value); setFeedback(""); }} /> : undefined}
    </GuestProfileDialog>
  );
};

export default BookerCustomerCreatePopup;
